/**
 * Kai Floor Plan design tokens — the Kai v2 design language as TS constants
 * (see ../Kai-v2-style-guide/Kai_Styleguide.html). THE single styling source
 * for the editor now that styles are Emotion, co-located in component files
 * (state-management plan Phase 5). No CSS custom properties: the editor
 * embeds in the main Kai app and must ship zero global style surface.
 */

/** ---- Kai v2 UI palette (7 colors) ---- */
export const white = '#ffffff'; // content surfaces — cards, room fill
export const paper = '#fffdf8'; // app background / canvas
export const bone = '#ddd6c8'; // hairlines, filled surfaces
export const error = '#c0443a'; // destructive / attention
export const midtone = '#7d7469'; // secondary text, muted UI
export const midtoneDark = '#645e55'; // secondary text that sits over a drawing
export const lemon = '#fdff86'; // primary action
export const ink = '#1a1a1a'; // text, heavy structure

/**
 * ---- THE 2D-editor selection color ----
 * Every selection visual (selected edges, selected handle fills, selected
 * free elements) references this ONE constant: change it here and nowhere
 * else. Placeholder blue (the pre-reskin river blue); swap for the final
 * blue once design delivers it.
 */
export const selection = '#40879f';
/** The selection color as a light wash over a drawing (the minimap's window). */
export const selectionTint = 'rgba(64, 135, 159, 0.14)';

/**
 * Editor content color beyond the 7-color UI palette: objects (furniture /
 * appliances) & window glass. Shared with the 3D view so both views read as
 * the same room — keep in sync with src/view3d/palette.ts.
 */
export const kaiBlue = '#95d5eb';

/**
 * ---- Type roles ----
 * Font loading is the host's job; these name the families as the Kai app
 * registers them ("CircularXX" is its Circular face) with generic fallbacks.
 * Every text in the editor — 2D plan, panel, and the 3D views' label layer —
 * draws from these, never from an inline family name.
 */
export const fDisplay = '"Maxeville", "CircularXX", "Helvetica Neue", sans-serif';
export const fBody = '"CircularXX", "Helvetica Neue", sans-serif';
export const fMono = '"Maxeville Construct", ui-monospace, "SF Mono", Menlo, monospace';

/** ---- Editing canvas (drafting paper) ---- */
export const canvasBg = paper;
export const roomStroke = ink; // walls — ink structure
export const accentSoft = 'rgba(253, 255, 134, 0.5)'; // lemon tint hover
export const objectFill = kaiBlue;
export const handleStroke = ink; // handle outlines (chrome, not selection)
export const counterFill = bone;
/**
 * The wall band's fill between the plan's inner and outer wall lines: bone at
 * 55% over the white slab, flattened to one opaque color so the band pieces
 * that overlap inside a corner (square-ended walls) never darken it.
 */
export const wallFill = '#ece8e1';
export const openColor = error; // destructive / delete
export const dimColor = ink; // dimension lines + labels
export const focusRing = '0 0 0 3px rgba(253, 255, 134, 0.55)';

/** ---- Drop shadows: ink at 18%, larger the higher a thing floats ---- */
const floatingShadowColor = 'rgba(26, 26, 26, 0.18)';
/** A popup card or menu (the option menu, the view widget's cards). */
export const menuShadow = `0 8px 24px ${floatingShadowColor}`;
/** A widget floating over the stage (the view widget, the minimap). */
export const widgetShadow = `0 4px 18px ${floatingShadowColor}`;
/** A floating button (the + add-element button). */
export const buttonShadow = `0 4px 9px ${floatingShadowColor}`;
/** A dimension chip, barely lifted off the plan. */
export const chipShadow = '0 1px 2px rgba(26, 26, 26, 0.08)';
/** The width of every Details panel field's control: lengths, numbers, the level picker. */
export const fieldWidth = '98px';

/** Mono label voice: uppercase, letterspaced, bold (labels, buttons, tabs). A fragment to interpolate into css templates. */
export const monoLabel = `
  font-family: ${fMono};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;
